import { http } from "@/lib/api";
import { toast } from "sonner";
import PaymentQRSettings from "@/pages/admin/dashboard/settings/PaymentQRSettings";
import PropertyContactSettings from "@/pages/admin/dashboard/settings/PropertyContactSettings";
import PropertyInfoSettings from "@/pages/admin/dashboard/settings/PropertyInfoSettings";
import NotificationSettings from "@/pages/admin/dashboard/settings/NotificationSettings";
import AccountSecurity from "@/pages/admin/dashboard/settings/AccountSecurity";
import IDRequirementSettings from "@/pages/admin/dashboard/settings/IDRequirementSettings";

export default function SettingsTab({ prop, reviewCfg, setReviewCfg, brandCfg, setBrandCfg }) {
  const saveBranding = async () => {
    try {
      const r = await http.put("/admin/settings/branding", { logo_url: brandCfg });
      setBrandCfg(r.data.logo_url);
      toast.success("Logo updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed (owner only)");
    }
  };

  const saveReview = async () => {
    try {
      const r = await http.put("/admin/settings/google-review", reviewCfg);
      setReviewCfg(r.data);
      toast.success("Review settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  return (
    <div className="grid md:grid-cols-2 gap-6" data-testid="settings-tab">
      <PropertyInfoSettings prop={prop} />
      <div className="card">
        <h3 className="font-serif text-2xl mb-3">Integrations</h3>
        <div className="space-y-3 text-sm">
          <div className="flex justify-between items-center"><span>WhatsApp confirmations</span><span className="badge-terracotta">NOT CONNECTED</span></div>
          <p className="text-xs" style={{ color: "var(--muted)" }}>Requires a WhatsApp Business API provider (e.g. Meta Cloud API / Twilio) credentials.</p>
          <div className="flex justify-between items-center"><span>Email confirmations</span><span className="badge-terracotta">NOT CONNECTED</span></div>
          <p className="text-xs" style={{ color: "var(--muted)" }}>Requires an email provider (e.g. Resend / SMTP) credentials.</p>
          <div className="flex justify-between items-center"><span>Airbnb calendar sync</span><span className="badge-pine">MANUAL</span></div>
          <div className="flex justify-between items-center"><span>Booking.com calendar sync</span><span className="badge-pine">MANUAL</span></div>
          <p className="text-xs" style={{ color: "var(--muted)" }}>Confirmed direct bookings raise an "OTA action required" alert; block the dates on each platform and mark them on the booking.</p>
        </div>
      </div>
      {brandCfg !== null && (
        <div className="card" data-testid="branding-settings">
          <h3 className="font-serif text-2xl mb-3">Branding</h3>
          <p className="text-xs mb-3" style={{ color: "var(--muted)" }}>The official logo is managed centrally — one change updates the website, guest portal, staff panels and PDF vouchers.</p>
          <img src={brandCfg} alt="Freebird Nest" className="h-12 object-contain rounded-lg border p-2 mb-3" style={{ borderColor: "var(--border)", background: "#fff" }} data-testid="branding-preview" />
          <input data-testid="branding-url" value={brandCfg} className="mb-3"
            onChange={(e) => setBrandCfg(e.target.value)} />
          <button data-testid="branding-save" className="btn-primary" onClick={saveBranding}>Save Logo</button>
        </div>
      )}
      {reviewCfg && (
        <div className="card" data-testid="review-settings">
          <h3 className="font-serif text-2xl mb-3">Google Review Settings</h3>
          <label className="flex items-center gap-3 mb-4 text-sm cursor-pointer">
            <input type="checkbox" style={{ width: "auto" }} data-testid="review-enabled"
              checked={reviewCfg.enabled}
              onChange={(e) => setReviewCfg({ ...reviewCfg, enabled: e.target.checked })} />
            Show the review request after checkout
          </label>
          <label className="text-xs block mb-1" style={{ color: "var(--muted)" }}>Google Review URL</label>
          <input data-testid="review-url" value={reviewCfg.url} className="mb-3"
            onChange={(e) => setReviewCfg({ ...reviewCfg, url: e.target.value })} />
          <label className="text-xs block mb-1" style={{ color: "var(--muted)" }}>Review QR image URL</label>
          <input data-testid="review-qr-url" value={reviewCfg.qr_image_url} placeholder="Paste the uploaded QR image link" className="mb-3"
            onChange={(e) => setReviewCfg({ ...reviewCfg, qr_image_url: e.target.value })} />
          {reviewCfg.qr_image_url && (
            <img src={reviewCfg.qr_image_url} alt="Google review QR" className="w-28 h-28 object-contain rounded-lg border mb-3" style={{ borderColor: "var(--border)" }} data-testid="review-qr-preview" />
          )}
          <button data-testid="review-save" className="btn-primary" onClick={saveReview}>Save Review Settings</button>
        </div>
      )}
      <PropertyContactSettings prop={prop} />
      <PaymentQRSettings />
      <NotificationSettings />
      <IDRequirementSettings />
      <AccountSecurity />
    </div>
  );
}
